import React, { useContext, createContext, useState } from "react";
import { useBluelagoon } from "@bluelagoon";

import { toast } from "sonner";

const workflowContext = createContext();

function useProvideWorkflow({ workflowId }) {
    const [steps, setSteps] = React.useState([]);
    const [workflow, setWorkflow] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const timerToast = React.useRef(null);
    const timerUpdateWorkflow = React.useRef(null);
    const timerUpdateWorkflowFields = React.useRef(null);
    const pendingWorkflowUpdate = React.useRef(null);
    const timerUpdateStep = React.useRef(null);
    const lastIdRef = React.useRef(null);

    const [testData, setTestData] = useState(null);

    const {
        call: createStep,
        isLoading: isLoadingNewStep,
        error: errorNewStep,
    } = useBluelagoon({
        method: "createWorkflowStep",
    });

    const {
        call: fetchWorkflow,
        isLoading: isLoadingWorkflow,
        error: errorWorkflow,
    } = useBluelagoon({
        method: "workflow",
    });

    const {
        call: deleteStep,
        isLoading: isLoadingDeleteStep,
        error: errorDeleteStep,
    } = useBluelagoon({
        method: "deleteWorkflowStep",
    });

    const {
        call: updateStep,
        isLoading: isLoadingUpdateStep,
        error: errorUpdateStep,
    } = useBluelagoon({
        method: "updateWorkflowStep",
    });

    const {
        call: updateWorkflowCall,
        isLoading: isLoadingUpdateWorkflow,
        error: errorUpdateWorkflow,
    } = useBluelagoon({
        method: "updateWorkflow",
    });

    const {
        call: addFileToWorkflow,
        isLoading: isLoadingAddFileToWorkflow,
        error: errorAddFileToWorkflow,
    } = useBluelagoon({
        method: "addFileToWorkflow",
    });

    const {
        call: togglePublish,
        error: publishError,
        isLoading: isPublishing,
    } = useBluelagoon({
        method: "togglePublishApp",
    });

    const {
        call: getStepVersions,
        isLoading: isLoadingStepVersions,
        error: errorStepVersions,
    } = useBluelagoon({
        method: "getWorkflowStepVersions",
    });

    const {
        call: getStepVersion,
        isLoading: isLoadingStepVersion,
        error: errorStepVersion,
    } = useBluelagoon({
        method: "getWorkflowStepVersion",
    });

    const hasLoadedRef = React.useRef(false);

    // Fetch workflow data
    const getWorkflow = React.useCallback(async () => {
        try {
            // Only show loading on initial fetch, not on refreshes
            if (!hasLoadedRef.current) {
                setIsLoading(true);
            }
            const response = await fetchWorkflow({ id: workflowId });

            if (response?.data) {
                hasLoadedRef.current = true;
                setWorkflow(response.data);
                setSteps(response.data.steps || []);

                setTestData(response.data.testData ?? {});
            }
        } catch (err) {
            setError(err);
            console.error("Error fetching workflow:", err);
        } finally {
            setIsLoading(false);
        }
    }, [workflowId, fetchWorkflow]);

    const handleTogglePublish = React.useCallback(async () => {
        await togglePublish({
            appId: workflow?.appId,
        });
        getWorkflow(); // Refresh app data
    }, [workflow?.appId, togglePublish, getWorkflow]);

    const addStep = React.useCallback(
        async (type, config = {}) => {
            try {
                const initialConfig = {
                    title: "Code Editor",
                    code: "",
                    language: "javascript",
                };

                const response = await createStep({
                    workflowId,
                    type,
                    config: { ...initialConfig, ...config },
                    isDraft: true,
                    name: type,
                });

                if (response?.workflowStep) {
                    setSteps((prevSteps) => [
                        ...prevSteps,
                        response.workflowStep,
                    ]);
                    return response.workflowStep;
                }
            } catch (error) {
                console.error("Error adding step:", error);
            }
        },
        [workflowId, createStep]
    );

    const pasteConfig = React.useCallback(async () => {
        try {
            const config = await navigator.clipboard.readText();

            const { type, name, config: configData } = JSON.parse(config);
            const response = await createStep({
                workflowId,
                type,
                config: configData,
                isDraft: true,
                name,
            });

            if (response?.workflowStep) {
                setSteps((prevSteps) => [...prevSteps, response.workflowStep]);
            }
        } catch (error) {
            console.error("Error pasting config:", error);
        }
    }, [workflowId, createStep]);

    const updateWorkflowStepType = React.useCallback(
        async (stepId, type) => {
            await updateStep({ stepId, type, isDraft: true });
            getWorkflow();
        },
        [updateStep]
    );

    const updateWorkflowStepName = React.useCallback(
        async (stepId, name) => {
            await updateStep({ stepId, name, isDraft: true });
            getWorkflow();
        },
        [updateStep]
    );

    const updateWorkflowStep = React.useCallback(
        async (stepId, updates) => {
            try {
                if (timerUpdateStep.current) {
                    clearTimeout(timerUpdateStep.current);
                }
                timerUpdateStep.current = setTimeout(async () => {
                    const response = await updateStep({
                        stepId,
                        ...updates,
                    });

                    if (response?.workflowStep) {
                        setSteps((prevSteps) =>
                            prevSteps.map((step) =>
                                step.id === stepId
                                    ? { ...step, ...response.workflowStep }
                                    : step
                            )
                        );
                        return response.workflowStep;
                    }
                }, 1000);
            } catch (error) {
                toast.error("Error updating step");
                console.error("Error updating step:", error);
            }
        },
        [updateStep]
    );

    const updateWorkflow = React.useCallback(
        (updates) => {
            return new Promise((resolve, reject) => {
                // Supersede any pending debounced update — the prior awaiter
                // resolves with undefined (its call was replaced, not failed).
                if (timerUpdateWorkflowFields.current) {
                    clearTimeout(timerUpdateWorkflowFields.current);
                    pendingWorkflowUpdate.current?.resolve(undefined);
                }
                pendingWorkflowUpdate.current = { resolve, reject };
                timerUpdateWorkflowFields.current = setTimeout(async () => {
                    const handlers = pendingWorkflowUpdate.current;
                    pendingWorkflowUpdate.current = null;
                    try {
                        const response = await updateWorkflowCall({
                            id: workflowId,
                            ...updates,
                        });
                        if (response?.workflow) {
                            setWorkflow(response.workflow);
                        }
                        handlers?.resolve(response?.workflow);
                    } catch (error) {
                        console.error("Error updating workflow:", error);
                        handlers?.reject(error);
                    }
                }, 1000);
            });
        },
        [workflowId, updateWorkflowCall]
    );

    const handleFileUpload = React.useCallback(
        async (file) => {
            try {
                const response = await addFileToWorkflow({
                    workflowId,
                    fileIds: [file?.id],
                });
            } catch (error) {
                console.error("Error adding file to workflow:", error);
            }

            getWorkflow();
        },
        [workflowId, addFileToWorkflow, getWorkflow]
    );

    const handleUpdateTestData = React.useCallback(
        async (testData) => {
            setTestData(testData);
            if (isLoading || testData === "" || !testData) return;

            try {
                JSON.stringify(testData);
            } catch (error) {
                if (timerToast.current) {
                    clearTimeout(timerToast.current);
                }
                timerToast.current = setTimeout(() => {
                    toast.error("Invalid JSON");
                }, 1000);
                return;
            }
            if (timerUpdateWorkflow.current) {
                clearTimeout(timerUpdateWorkflow.current);
            }
            timerUpdateWorkflow.current = setTimeout(async () => {
                setIsLoading(true);
                await updateWorkflowCall({
                    id: workflowId,
                    testData: testData,
                });
                setIsLoading(false);
            }, 1000);
        },
        [workflowId, updateWorkflowCall]
    );

    // Version history methods
    const getWorkflowStepVersions = React.useCallback(
        async (stepId, options = {}) => {
            try {
                const { limit = 50, offset = 0 } = options;
                const response = await getStepVersions({
                    stepId,
                    limit,
                    offset,
                });

                if (response?.data) {
                    return response.data;
                }
            } catch (error) {
                console.error("Error fetching step versions:", error);
                toast.error("Failed to fetch version history");
            }
        },
        [getStepVersions]
    );

    const getWorkflowStepVersion = React.useCallback(
        async (stepId, versionId) => {
            try {
                const response = await getStepVersion({
                    stepId,
                    versionId,
                });

                if (response?.data) {
                    return response.data;
                }
            } catch (error) {
                console.error("Error fetching step version:", error);
                toast.error("Failed to fetch version details");
            }
        },
        [getStepVersion]
    );

    React.useEffect(() => {
        return () => {
            if (timerUpdateWorkflowFields.current) {
                clearTimeout(timerUpdateWorkflowFields.current);
                timerUpdateWorkflowFields.current = null;
            }
            pendingWorkflowUpdate.current?.resolve(undefined);
            pendingWorkflowUpdate.current = null;
        };
    }, []);

    // Fetch workflow on mount
    React.useEffect(() => {
        if (workflowId) {
            lastIdRef.current = workflowId;
            try { localStorage.setItem("last_workflow_id", workflowId); } catch (_) {}
            getWorkflow();
        } else if (lastIdRef.current && !workflowId) {
            hasLoadedRef.current = false;
            setWorkflow(null);
            setSteps([]);
            setTestData(null);
            setError(null);
            setIsLoading(false);
        }
    }, [workflowId, getWorkflow]);

    return {
        workflowId,
        workflow,
        steps,
        setSteps,
        addStep,
        pasteConfig,
        isLoading,
        error,
        isLoadingNewStep,
        errorNewStep,
        refresh: getWorkflow,
        refetchWorkflow: getWorkflow,
        deleteStep,
        isLoadingDeleteStep,
        errorDeleteStep,
        updateWorkflowStep,
        isLoadingUpdateStep,
        errorUpdateStep,
        updateWorkflow,
        isLoadingUpdateWorkflow,
        errorUpdateWorkflow,
        updateWorkflowStepType,

        handleFileUpload,
        testData,
        setTestData: handleUpdateTestData,
        handleTogglePublish,
        // Version history methods
        getWorkflowStepVersions,
        getWorkflowStepVersion,

        // Version history loading states
        isLoadingStepVersions,
        isLoadingStepVersion,

        // Version history errors
        errorStepVersions,
        errorStepVersion,

        // Update step name
        updateWorkflowStepName,
    };
}

export function ProvideWorkflow({ children, workflowId }) {
    const workflow = useProvideWorkflow({ workflowId });
    return (
        <workflowContext.Provider value={workflow}>
            {children}
        </workflowContext.Provider>
    );
}

export function useWorkflow() {
    return useContext(workflowContext);
}
